import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';
import { clientsApi } from '@/api/clients';
import { clientSchema } from '@/utils/validators';
import FormField from '@/components/FormField';
import LoadingSpinner from '@/components/LoadingSpinner';

const clientFormSchema = clientSchema.extend({
  active: z.boolean().default(true),
});

type ClientFormData = z.infer<typeof clientFormSchema>;

export function ClientForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editId = id && id !== 'new' ? id : null;
  const isEditing = !!editId;

  const { data: response, isLoading: loadingClient } = useQuery({
    queryKey: ['client', editId],
    queryFn: () => clientsApi.getById(editId!),
    enabled: isEditing,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: '',
      company: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
      active: true,
    },
  });

  useEffect(() => {
    if (isEditing && response?.data?.data) {
      const client = response.data.data;
      reset({
        name: client.name || '',
        company: client.company || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        notes: client.notes || '',
        active: client.active ?? true,
      });
    }
  }, [isEditing, response, reset]);

  const createMutation = useMutation({
    mutationFn: (data: ClientFormData) => clientsApi.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Cliente creado exitosamente');
      navigate(`/clients/${res.data.data.id}`);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al crear el cliente');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ClientFormData) => clientsApi.update(editId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', editId] });
      toast.success('Cliente actualizado exitosamente');
      navigate(`/clients/${editId}`);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al actualizar el cliente');
    },
  });

  const onSubmit = async (data: ClientFormData) => {
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isEditing && loadingClient) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-md p-2 text-surface-400 hover:bg-surface-100 hover:text-surface-600 transition-colors"
          aria-label="Volver"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-surface-900">
          {isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="rounded-lg border border-surface-200 bg-white p-6 shadow-sm space-y-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <FormField label="Nombre" error={errors.name?.message} required>
            <input
              type="text"
              {...register('name')}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Nombre del cliente"
            />
          </FormField>

          <FormField label="Empresa" error={errors.company?.message}>
            <input
              type="text"
              {...register('company')}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Nombre de la empresa"
            />
          </FormField>

          <FormField label="Correo Electrónico" error={errors.email?.message}>
            <input
              type="email"
              {...register('email')}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="correo@ejemplo.com"
            />
          </FormField>

          <FormField label="Teléfono" error={errors.phone?.message}>
            <input
              type="text"
              {...register('phone')}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="+52 555 123 4567"
            />
          </FormField>
        </div>

        <FormField label="Dirección" error={errors.address?.message}>
          <textarea
            {...register('address')}
            rows={3}
            className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="Dirección completa"
          />
        </FormField>

        <FormField label="Notas" error={errors.notes?.message}>
          <textarea
            {...register('notes')}
            rows={3}
            className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="Notas adicionales..."
          />
        </FormField>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            {...register('active')}
            className="h-4 w-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-surface-700">Cliente activo</span>
        </label>

        <div className="flex items-center justify-end gap-3 border-t border-surface-200 pt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md border border-surface-300 bg-white px-4 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {isEditing ? 'Actualizando...' : 'Guardando...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEditing ? 'Actualizar' : 'Guardar'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ClientForm;
